import React, { useState, forwardRef, useEffect, useRef } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./DateTimePicker.module.css";
import {
  format,
  getYear,
  getMonth,
  setHours,
  setMinutes,
  isAfter,
  isBefore,
  startOfDay,
  isSameDay,
} from "date-fns";
import { AiFillCloseCircle } from "react-icons/ai";

// Define props manually to avoid complex typing issues
interface CustomDateTimePickerProps {
  label: string;
  startDate: Date | null;
  endDate: Date | null;
  onChange: (dates: [Date | null, Date | null]) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  placeholderText?: string;
  className?: string;
  minStartTime?: Date; // Minimum time for start date (e.g., registration end time)
}

const generateTimeOptions = (interval: number) => {
  const times = [];
  for (let i = 0; i < 24 * 60; i += interval) {
    const hours = Math.floor(i / 60);
    const minutes = i % 60;
    const h = hours % 12 === 0 ? 12 : hours % 12;
    const m = minutes.toString().padStart(2, "0");
    const p = hours < 12 ? "AM" : "PM";
    times.push(`${h}:${m} ${p}`);
  }
  return times;
};

const CustomTimeDropdown: React.FC<{
  options: string[];
  selected: string;
  onChange: (value: string) => void;
  styles: any;
  disabled?: boolean;
  disabledOptions?: string[];
}> = ({ options, selected, onChange, styles, disabled = false, disabledOptions = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter out disabled options - only show valid options
  const validOptions = options.filter(option => !disabledOptions.includes(option));

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  // Auto-scroll to selected option when dropdown opens
  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedIndex = validOptions.indexOf(selected);
      if (selectedIndex !== -1) {
        const optionElement = listRef.current.children[selectedIndex] as HTMLElement;
        if (optionElement) {
          // Calculate the scroll position to center the selected option
          const containerHeight = listRef.current.clientHeight;
          const optionHeight = optionElement.offsetHeight;
          const optionOffsetTop = optionElement.offsetTop;

          // Calculate scroll position to center the option
          const scrollTop = optionOffsetTop - containerHeight / 2 + optionHeight / 2;

          // Scroll smoothly within the container only
          listRef.current.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: "smooth",
          });
        }
      }
    }
  }, [isOpen, selected, validOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={styles["custom-time-dropdown"]} ref={dropdownRef}>
      <div
        className={`${styles["time-select-display"]} ${disabled ? styles["disabled"] : ""} ${isOpen ? styles["open"] : ""}`}
        onClick={handleToggle}
        style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
      >
        {selected}
      </div>
      {isOpen && !disabled && (
        <div className={styles["time-options-list"]} ref={listRef}>
          {validOptions.length > 0 ? (
            validOptions.map(option => (
              <div
                key={option}
                className={`${styles["time-option"]} ${
                  option === selected ? styles["selected-option"] : ""
                }`}
                onClick={() => handleSelect(option)}
              >
                {option}
              </div>
            ))
          ) : (
            <div className={styles["no-options"]}>No valid time options available</div>
          )}
        </div>
      )}
    </div>
  );
};

const CustomInput = forwardRef<
  HTMLDivElement,
  {
    onClick?: () => void;
    startDate: Date | null;
    endDate: Date | null;
    startTime: string;
    endTime: string;
    value?: string;
    placeholderText?: string;
    onClear?: () => void;
  }
>(({ onClick, startDate, endDate, startTime, endTime, placeholderText, onClear }, ref) => {
  const hasValue = startDate && endDate;

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  return (
    <div
      className="purple-field d-flex align-items-center w-100 border-0 px-3 text-white fw-bold fs-12 height-45 position-relative"
      onClick={onClick}
      ref={ref}
    >
      <div className="flex-grow-1 text-white fw-bold">
        {hasValue
          ? `${format(startDate, "dd/MM/yyyy")} ${startTime} - ${format(
              endDate,
              "dd/MM/yyyy"
            )} ${endTime}`
          : placeholderText || "Select Dates & Time of Registration"}
      </div>
      {hasValue && (
        <AiFillCloseCircle
          className="btn-size-20 text-white fw-bold cursor-pointer flex-shrink-0 ms-2"
          style={{
            minWidth: "20px",
            transition: "opacity 0.2s ease",
          }}
          onClick={handleClear}
          onMouseEnter={e => {
            e.currentTarget.style.opacity = "0.8";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.opacity = "1";
          }}
        />
      )}
    </div>
  );
});

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
  label,
  startDate,
  endDate,
  onChange,
  placeholderText,
  minStartTime,
  ...rest
}) => {
  const timeOptions = generateTimeOptions(15);

  const formatAndRoundTime = (date: Date | null) => {
    if (!date) return "12:00 AM";
    const minutes = date.getMinutes();
    // Round up to next 15-minute interval
    const roundedMinutes = Math.ceil(minutes / 15) * 15;

    let newDate = new Date(date.getTime());
    if (roundedMinutes === 60) {
      newDate.setHours(newDate.getHours() + 1);
      newDate.setMinutes(0);
    } else {
      newDate.setMinutes(roundedMinutes);
    }
    return format(newDate, "h:mm aa");
  };

  const [startTime, setStartTime] = useState(formatAndRoundTime(startDate));
  const [endTime, setEndTime] = useState(formatAndRoundTime(endDate));

  // Track if user has manually adjusted times to prevent auto-overriding
  const [hasManualStartTime, setHasManualStartTime] = useState(false);
  const [hasManualEndTime, setHasManualEndTime] = useState(false);

  useEffect(() => {
    if (!hasManualStartTime) {
      setStartTime(formatAndRoundTime(startDate));
    }
  }, [startDate, hasManualStartTime]);

  useEffect(() => {
    if (!hasManualEndTime) {
      setEndTime(formatAndRoundTime(endDate));
    }
  }, [endDate, hasManualEndTime]);

  const handleClear = () => {
    setStartTime("12:00 AM");
    setEndTime("12:00 AM");
    setHasManualStartTime(false);
    setHasManualEndTime(false);
    onChange([null, null]);
  };

  const applyTimeToDate = (date: Date | null, time: string): Date | null => {
    if (!date) return null;

    // Create a new date to avoid mutating the original
    const newDate = new Date(date.getTime());

    const [timeStr, period] = time.split(" ");
    let [hours, minutes] = timeStr.split(":").map(Number);

    // Convert to 24-hour format
    if (period === "PM" && hours < 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;

    return setMinutes(setHours(newDate, hours), minutes);
  };

  // Function to get disabled time options for start time
  const getDisabledStartTimeOptions = (): string[] => {
    if (!startDate) return [];

    const now = new Date();
    const isToday = isSameDay(startDate, now);
    const isMinDateToday = minStartTime && isSameDay(startDate, minStartTime);

    if (!isToday && !isMinDateToday) return [];

    const disabledTimes: string[] = [];

    // If it's today, disable past times (including current time)
    if (isToday) {
      const currentTime = now.getHours() * 60 + now.getMinutes();

      timeOptions.forEach(timeOption => {
        const [timeStr, period] = timeOption.split(" ");
        let [hours, minutes] = timeStr.split(":").map(Number);

        if (period === "PM" && hours < 12) hours += 12;
        if (period === "AM" && hours === 12) hours = 0;

        const optionTime = hours * 60 + minutes;

        if (optionTime <= currentTime) {
          disabledTimes.push(timeOption);
        }
      });
    }

    // If it's the same day as minStartTime, disable times before minStartTime
    if (isMinDateToday && minStartTime) {
      const minTime = minStartTime.getHours() * 60 + minStartTime.getMinutes();

      timeOptions.forEach(timeOption => {
        const [timeStr, period] = timeOption.split(" ");
        let [hours, minutes] = timeStr.split(":").map(Number);

        if (period === "PM" && hours < 12) hours += 12;
        if (period === "AM" && hours === 12) hours = 0;

        const optionTime = hours * 60 + minutes;

        if (optionTime <= minTime) {
          disabledTimes.push(timeOption);
        }
      });
    }

    return disabledTimes;
  };

  // Function to get disabled time options for end time
  const getDisabledEndTimeOptions = (): string[] => {
    if (!endDate || !startDate) return [];

    const now = new Date();
    const disabledTimes: string[] = [];
    const isSameDate = isSameDay(startDate, endDate);
    const isToday = isSameDay(endDate, now);

    // If it's today, also disable past times
    if (isToday) {
      const currentTime = now.getHours() * 60 + now.getMinutes();

      timeOptions.forEach(timeOption => {
        const [timeStr, period] = timeOption.split(" ");
        let [hours, minutes] = timeStr.split(":").map(Number);

        if (period === "PM" && hours < 12) hours += 12;
        if (period === "AM" && hours === 12) hours = 0;

        const optionTime = hours * 60 + minutes;

        if (optionTime <= currentTime) {
          disabledTimes.push(timeOption);
        }
      });
    }

    if (!isSameDate) return disabledTimes;

    // If same date, disable times before or equal to start time
    const [startTimeStr, startPeriod] = startTime.split(" ");
    let [startHours, startMinutes] = startTimeStr.split(":").map(Number);

    if (startPeriod === "PM" && startHours < 12) startHours += 12;
    if (startPeriod === "AM" && startHours === 12) startHours = 0;

    const startTimeInMinutes = startHours * 60 + startMinutes;

    timeOptions.forEach(timeOption => {
      const [timeStr, period] = timeOption.split(" ");
      let [hours, minutes] = timeStr.split(":").map(Number);

      if (period === "PM" && hours < 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;

      const optionTime = hours * 60 + minutes;

      if (optionTime <= startTimeInMinutes) {
        disabledTimes.push(timeOption);
      }
    });

    return disabledTimes;
  };

  const validateDateRange = (start: Date | null, end: Date | null): [Date | null, Date | null] => {
    if (!start || !end) return [start, end];

    // Ensure end date is not before start date
    if (isBefore(startOfDay(end), startOfDay(start))) {
      // If end date is before start date, reset the range
      return [start, null];
    }

    return [start, end];
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    let [start, end] = dates;

    // Validate the date range
    [start, end] = validateDateRange(start, end);

    // Special case: If user selects the same date as start date for end date
    if (
      start &&
      end &&
      startDate &&
      startOfDay(start).getTime() === startOfDay(startDate).getTime() &&
      startOfDay(end).getTime() === startOfDay(start).getTime() &&
      !hasManualEndTime
    ) {
      // User clicked on the same date for end, auto-set end time to start time + 15 minutes
      const startWithTime = applyTimeToDate(start, startTime);
      if (startWithTime) {
        const autoEndTime = new Date(startWithTime.getTime() + 15 * 60 * 1000);
        const autoEndTimeStr = format(autoEndTime, "h:mm aa");
        setEndTime(autoEndTimeStr);
        onChange([startWithTime, autoEndTime]);
        return;
      }
    }

    // Handle edge case: if user selects today's date, round the time to next 15-minute interval
    const now = new Date();
    const today = startOfDay(now);

    // Handle switching from future date to today's date - auto-adjust time if it would be in the past
    if (start && startOfDay(start).getTime() === today.getTime()) {
      const currentTimeWithSelectedTime = applyTimeToDate(start, startTime);

      // If the current time selection would be in the past, auto-adjust to next valid time
      if (currentTimeWithSelectedTime && currentTimeWithSelectedTime <= now) {
        // Calculate next valid time slot - round up current time to next 15-minute interval
        const currentMinutes = now.getMinutes();
        const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

        let roundedTime = new Date(now.getTime());
        if (roundedMinutes === 60) {
          roundedTime.setHours(roundedTime.getHours() + 1);
          roundedTime.setMinutes(0);
        } else {
          roundedTime.setMinutes(roundedMinutes);
        }

        // If the rounded time is still equal to current time, add 15 minutes
        if (roundedTime.getTime() <= now.getTime()) {
          roundedTime = new Date(roundedTime.getTime() + 15 * 60 * 1000);
        }

        const roundedTimeStr = format(roundedTime, "h:mm aa");
        setStartTime(roundedTimeStr);
        setHasManualStartTime(false); // Reset manual flag since we're auto-adjusting

        const startWithAdjustedTime = applyTimeToDate(start, roundedTimeStr);

        // Handle end date adjustments if needed
        if (end) {
          const endWithTime = applyTimeToDate(end, endTime);

          // If both dates are the same day and end time needs adjustment
          if (
            startWithAdjustedTime &&
            endWithTime &&
            startOfDay(startWithAdjustedTime).getTime() === startOfDay(endWithTime).getTime() &&
            (isBefore(endWithTime, startWithAdjustedTime) ||
              endWithTime.getTime() === startWithAdjustedTime.getTime())
          ) {
            const adjustedEndTime = new Date(startWithAdjustedTime.getTime() + 15 * 60 * 1000);
            const adjustedEndTimeStr = format(adjustedEndTime, "h:mm aa");
            setEndTime(adjustedEndTimeStr);
            setHasManualEndTime(false);
            onChange([startWithAdjustedTime, adjustedEndTime]);
            return;
          }

          onChange([startWithAdjustedTime, endWithTime]);
          return;
        }

        onChange([startWithAdjustedTime, null]);
        return;
      }
    }

    // Check if we need to auto-adjust start time based on minStartTime
    if (
      start &&
      minStartTime &&
      startOfDay(start).getTime() === startOfDay(minStartTime).getTime() &&
      !hasManualStartTime
    ) {
      // User selected the same date as minStartTime for the first time, auto-adjust start time to be after minStartTime
      const adjustedStartTime = new Date(minStartTime.getTime() + 15 * 60 * 1000); // Add 15 minutes
      const adjustedStartTimeStr = format(adjustedStartTime, "h:mm aa");
      setStartTime(adjustedStartTimeStr);

      const startWithAdjustedTime = applyTimeToDate(start, adjustedStartTimeStr);

      // If we have an end date, ensure it's after the adjusted start time
      if (end) {
        const endWithTime = applyTimeToDate(end, endTime);

        // If both dates are the same day and end time is before or equal to adjusted start time
        if (
          startWithAdjustedTime &&
          endWithTime &&
          startOfDay(startWithAdjustedTime).getTime() === startOfDay(endWithTime).getTime() &&
          (isBefore(endWithTime, startWithAdjustedTime) ||
            endWithTime.getTime() === startWithAdjustedTime.getTime())
        ) {
          // Changed: Add 15 minutes instead of 1 hour
          const adjustedEndTime = new Date(startWithAdjustedTime.getTime() + 15 * 60 * 1000);
          const adjustedEndTimeStr = format(adjustedEndTime, "h:mm aa");
          setEndTime(adjustedEndTimeStr);
          onChange([startWithAdjustedTime, adjustedEndTime]);
          return;
        }

        onChange([startWithAdjustedTime, endWithTime]);
        return;
      }

      onChange([startWithAdjustedTime, null]);
      return;
    }

    if (start && startOfDay(start).getTime() === today.getTime() && !hasManualStartTime) {
      // User selected today's date for the first time, round current time to next valid 15-minute slot
      const currentMinutes = now.getMinutes();
      const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

      let roundedTime = new Date(now.getTime());
      if (roundedMinutes === 60) {
        roundedTime.setHours(roundedTime.getHours() + 1);
        roundedTime.setMinutes(0);
      } else {
        roundedTime.setMinutes(roundedMinutes);
      }

      // If the rounded time is still equal to current time, add 15 minutes
      if (roundedTime.getTime() <= now.getTime()) {
        roundedTime = new Date(roundedTime.getTime() + 15 * 60 * 1000);
      }

      const roundedTimeStr = format(roundedTime, "h:mm aa");
      setStartTime(roundedTimeStr);

      // Apply the rounded time to the start date
      const startWithRoundedTime = applyTimeToDate(start, roundedTimeStr);

      // If we have an end date, ensure it's after the rounded start time
      if (end) {
        const endWithTime = applyTimeToDate(end, endTime);

        // If both dates are the same day and end time is before or equal to rounded start time
        if (
          startWithRoundedTime &&
          endWithTime &&
          startOfDay(startWithRoundedTime).getTime() === startOfDay(endWithTime).getTime() &&
          (isBefore(endWithTime, startWithRoundedTime) ||
            endWithTime.getTime() === startWithRoundedTime.getTime())
        ) {
          // Changed: Add 15 minutes instead of 1 hour
          const adjustedEndTime = new Date(startWithRoundedTime.getTime() + 15 * 60 * 1000);
          const adjustedEndTimeStr = format(adjustedEndTime, "h:mm aa");
          setEndTime(adjustedEndTimeStr);
          onChange([startWithRoundedTime, adjustedEndTime]);
          return;
        }

        onChange([startWithRoundedTime, endWithTime]);
        return;
      }

      onChange([startWithRoundedTime, null]);
      return;
    }

    // Handle edge case: if user selects today's date as end date
    // Handle switching from future date to today's date for end date - auto-adjust time if it would be in the past
    if (end && startOfDay(end).getTime() === today.getTime()) {
      const currentEndTimeWithSelectedTime = applyTimeToDate(end, endTime);

      // If the current end time selection would be in the past, auto-adjust to next valid time
      if (
        currentEndTimeWithSelectedTime &&
        currentEndTimeWithSelectedTime <= now &&
        !hasManualEndTime
      ) {
        const currentMinutes = now.getMinutes();
        const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

        let roundedTime = new Date(now.getTime());
        if (roundedMinutes === 60) {
          roundedTime.setHours(roundedTime.getHours() + 1);
          roundedTime.setMinutes(0);
        } else {
          roundedTime.setMinutes(roundedMinutes);
        }

        // If the rounded time is still equal to current time, add 15 minutes
        if (roundedTime.getTime() <= now.getTime()) {
          roundedTime = new Date(roundedTime.getTime() + 15 * 60 * 1000);
        }

        const roundedTimeStr = format(roundedTime, "h:mm aa");
        setEndTime(roundedTimeStr);
        setHasManualEndTime(false); // Reset manual flag since we're auto-adjusting

        // Apply the rounded time to the end date
        const endWithRoundedTime = applyTimeToDate(end, roundedTimeStr);

        // If we have a start date, ensure end time is after start time
        if (start) {
          const startWithTime = applyTimeToDate(start, startTime);

          // If both dates are the same day and rounded end time is before or equal to start time
          if (
            startWithTime &&
            endWithRoundedTime &&
            startOfDay(startWithTime).getTime() === startOfDay(endWithRoundedTime).getTime() &&
            (isBefore(endWithRoundedTime, startWithTime) ||
              endWithRoundedTime.getTime() === startWithTime.getTime())
          ) {
            // Adjust end time to be 15 minutes after start time
            const adjustedEndTime = new Date(startWithTime.getTime() + 15 * 60 * 1000);
            const adjustedEndTimeStr = format(adjustedEndTime, "h:mm aa");
            setEndTime(adjustedEndTimeStr);
            onChange([startWithTime, adjustedEndTime]);
            return;
          }

          onChange([startWithTime, endWithRoundedTime]);
          return;
        }

        onChange([null, endWithRoundedTime]);
        return;
      }

      // Handle case where user selects today's date as end date for the first time (and time is not in past)
      if (!hasManualEndTime) {
        const currentMinutes = now.getMinutes();
        const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

        let roundedTime = new Date(now.getTime());
        if (roundedMinutes === 60) {
          roundedTime.setHours(roundedTime.getHours() + 1);
          roundedTime.setMinutes(0);
        } else {
          roundedTime.setMinutes(roundedMinutes);
        }

        // If the rounded time is still equal to current time, add 15 minutes
        if (roundedTime.getTime() <= now.getTime()) {
          roundedTime = new Date(roundedTime.getTime() + 15 * 60 * 1000);
        }

        const roundedTimeStr = format(roundedTime, "h:mm aa");
        setEndTime(roundedTimeStr);

        // Apply the rounded time to the end date
        const endWithRoundedTime = applyTimeToDate(end, roundedTimeStr);

        // If we have a start date, ensure end time is after start time
        if (start) {
          const startWithTime = applyTimeToDate(start, startTime);

          // If both dates are the same day and rounded end time is before or equal to start time
          if (
            startWithTime &&
            endWithRoundedTime &&
            startOfDay(startWithTime).getTime() === startOfDay(endWithRoundedTime).getTime() &&
            (isBefore(endWithRoundedTime, startWithTime) ||
              endWithRoundedTime.getTime() === startWithTime.getTime())
          ) {
            // Don't allow end time to be before or equal to start time on the same day
            return;
          }

          onChange([startWithTime, endWithRoundedTime]);
          return;
        }

        onChange([null, endWithRoundedTime]);
        return;
      }
    }

    // Apply times to dates (original logic for non-today dates)
    const startWithTime = applyTimeToDate(start, startTime);
    const endWithTime = applyTimeToDate(end, endTime);

    // If user selects the same date for both start and end, auto-adjust end time
    if (
      start &&
      end &&
      startOfDay(start).getTime() === startOfDay(end).getTime() &&
      startWithTime
    ) {
      // Check if this is a new selection where user picked the same date as start date
      // or if end time needs adjustment
      const shouldAutoAdjustEndTime =
        !endWithTime ||
        isBefore(endWithTime, startWithTime) ||
        endWithTime.getTime() === startWithTime.getTime() ||
        !hasManualEndTime; // Auto-adjust if user hasn't manually set end time yet

      if (shouldAutoAdjustEndTime) {
        // Set end time to 15 minutes after start time
        const adjustedEndTime = new Date(startWithTime.getTime() + 15 * 60 * 1000);
        const adjustedEndTimeStr = format(adjustedEndTime, "h:mm aa");
        setEndTime(adjustedEndTimeStr);
        setHasManualEndTime(false); // Reset manual flag since we're auto-adjusting
        onChange([startWithTime, adjustedEndTime]);
        return;
      }
    }

    // Additional validation: if both dates exist and end time is before or equal to start time on the same day
    if (
      startWithTime &&
      endWithTime &&
      startOfDay(startWithTime).getTime() === startOfDay(endWithTime).getTime() &&
      (isBefore(endWithTime, startWithTime) || endWithTime.getTime() === startWithTime.getTime())
    ) {
      // Changed: Add 15 minutes instead of 1 hour
      const adjustedEndTime = new Date(startWithTime.getTime() + 15 * 60 * 1000);
      setEndTime(format(adjustedEndTime, "h:mm aa"));
      onChange([startWithTime, adjustedEndTime]);
      return;
    }

    onChange([startWithTime, endWithTime]);
  };

  const handleTimeChange = (time: string, type: "start" | "end") => {
    const now = new Date();
    const today = startOfDay(now);

    if (type === "start") {
      setStartTime(time);
      setHasManualStartTime(true); // Mark that user manually changed start time
      if (startDate) {
        const newStartDate = applyTimeToDate(startDate, time);

        // Auto-adjust if user selects a past time for today's date
        if (
          newStartDate &&
          startOfDay(startDate).getTime() === today.getTime() &&
          newStartDate <= now
        ) {
          const currentMinutes = now.getMinutes();
          const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

          let roundedTime = new Date(now.getTime());
          if (roundedMinutes === 60) {
            roundedTime.setHours(roundedTime.getHours() + 1);
            roundedTime.setMinutes(0);
          } else {
            roundedTime.setMinutes(roundedMinutes);
          }

          // If the rounded time is still equal to current time, add 15 minutes
          if (roundedTime.getTime() <= now.getTime()) {
            roundedTime = new Date(roundedTime.getTime() + 15 * 60 * 1000);
          }

          const adjustedTimeStr = format(roundedTime, "h:mm aa");
          setStartTime(adjustedTimeStr);
          const adjustedStartDate = applyTimeToDate(startDate, adjustedTimeStr);

          // If we have both dates and they're on the same day, ensure end time is after start time
          if (
            adjustedStartDate &&
            endDate &&
            startOfDay(adjustedStartDate).getTime() === startOfDay(endDate).getTime()
          ) {
            const endDateTime = applyTimeToDate(endDate, endTime);
            if (
              endDateTime &&
              (isBefore(endDateTime, adjustedStartDate) ||
                endDateTime.getTime() === adjustedStartDate.getTime())
            ) {
              const adjustedEndTime = new Date(adjustedStartDate.getTime() + 15 * 60 * 1000);
              setEndTime(format(adjustedEndTime, "h:mm aa"));
              onChange([adjustedStartDate, adjustedEndTime]);
              return;
            }
          }

          onChange([adjustedStartDate, applyTimeToDate(endDate, endTime)]);
          return;
        }

        // If we have both dates and they're on the same day, ensure end time is after start time
        if (
          newStartDate &&
          endDate &&
          startOfDay(newStartDate).getTime() === startOfDay(endDate).getTime()
        ) {
          const endDateTime = applyTimeToDate(endDate, endTime);
          if (
            endDateTime &&
            (isBefore(endDateTime, newStartDate) ||
              endDateTime.getTime() === newStartDate.getTime())
          ) {
            // Changed: Add 15 minutes instead of 1 hour
            const adjustedEndTime = new Date(newStartDate.getTime() + 15 * 60 * 1000);
            setEndTime(format(adjustedEndTime, "h:mm aa"));
            onChange([newStartDate, adjustedEndTime]);
            return;
          }
        }

        onChange([newStartDate, applyTimeToDate(endDate, endTime)]);
      }
    } else {
      setEndTime(time);
      setHasManualEndTime(true); // Mark that user manually changed end time
      if (endDate) {
        const newEndDate = applyTimeToDate(endDate, time);

        // Auto-adjust if user selects a past time for today's date
        if (newEndDate && startOfDay(endDate).getTime() === today.getTime() && newEndDate <= now) {
          const currentMinutes = now.getMinutes();
          const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

          let roundedTime = new Date(now.getTime());
          if (roundedMinutes === 60) {
            roundedTime.setHours(roundedTime.getHours() + 1);
            roundedTime.setMinutes(0);
          } else {
            roundedTime.setMinutes(roundedMinutes);
          }

          // If the rounded time is still equal to current time, add 15 minutes
          if (roundedTime.getTime() <= now.getTime()) {
            roundedTime = new Date(roundedTime.getTime() + 15 * 60 * 1000);
          }

          const adjustedTimeStr = format(roundedTime, "h:mm aa");
          setEndTime(adjustedTimeStr);
          const adjustedEndDate = applyTimeToDate(endDate, adjustedTimeStr);

          onChange([applyTimeToDate(startDate, startTime), adjustedEndDate]);
          return;
        }

        // If we have both dates and they're on the same day, ensure end time is after start time
        if (
          newEndDate &&
          startDate &&
          startOfDay(newEndDate).getTime() === startOfDay(startDate).getTime()
        ) {
          const startDateTime = applyTimeToDate(startDate, startTime);
          if (
            startDateTime &&
            (isBefore(newEndDate, startDateTime) ||
              newEndDate.getTime() === startDateTime.getTime())
          ) {
            // Don't allow end time to be before or equal to start time on the same day
            return;
          }
        }

        onChange([applyTimeToDate(startDate, startTime), newEndDate]);
      }
    }
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className={styles["custom-datetime-picker"]}>
      <label className="fs-14 fw-600 text-white mb-1">{label}</label>
      <ReactDatePicker
        selected={startDate}
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateChange}
        selectsRange
        shouldCloseOnSelect={false}
        popperClassName={styles["date-picker-wrapper"]}
        customInput={
          <CustomInput
            startDate={startDate}
            endDate={endDate}
            startTime={startTime}
            endTime={endTime}
            placeholderText={placeholderText}
            onClear={handleClear}
          />
        }
        showTimeSelect={false}
        minDate={rest.minDate || new Date()} // Prevent selecting past dates
        dayClassName={date => {
          // Only highlight the exact start and end dates, not all dates in between
          if (startDate && date.getTime() === startOfDay(startDate).getTime()) {
            return styles["my-selected"];
          }
          if (endDate && date.getTime() === startOfDay(endDate).getTime()) {
            return styles["my-selected"];
          }
          // Highlight in-range dates only if both start and end are selected
          if (startDate && endDate) {
            const d = startOfDay(date).getTime();
            const s = startOfDay(startDate).getTime();
            const e = startOfDay(endDate).getTime();
            if (d > s && d < e) {
              return styles["my-in-range"];
            }
          }
          return "";
        }}
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className={styles["custom-header-container"]}>
            <button
              type="button"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              className={`${styles["arrow-btn"]} ${styles["prev-arrow"]}`}
            >
              {"<"}
            </button>

            <div className={styles["custom-header_month-year-container"]}>
              <span className={styles["month-display"]}>{months[getMonth(date)]}</span>
              <span className={styles["year-display"]}>{getYear(date)}</span>
            </div>

            <button
              type="button"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              className={`${styles["arrow-btn"]} ${styles["next-arrow"]}`}
            >
              {">"}
            </button>
          </div>
        )}
        {...rest}
      >
        <div className={styles["date-time-section"]} style={{ color: "black" }}>
          <div className={styles["date-time-row"]}>
            <div>
              <div className={`${styles["date-time-label"]} fw-bold`}>From</div>
              <div className={styles["date-time-value"]}>
                <div className={styles["date-time-date"]}>
                  {startDate ? format(startDate, "d") : "-"}
                  <div className={`${styles["date-time-day"]} fw-bold`}>
                    {startDate ? format(startDate, "eeee") : "Select Date"}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles["date-time-clock"]}>
              <CustomTimeDropdown
                options={timeOptions}
                selected={startTime}
                onChange={time => handleTimeChange(time, "start")}
                styles={styles}
                disabled={!startDate}
                disabledOptions={getDisabledStartTimeOptions()}
              />
            </div>
          </div>
          <div className={styles["date-time-row"]}>
            <div>
              <div className={`${styles["date-time-label"]} fw-bold`}>To</div>
              <div className={styles["date-time-value"]}>
                <div className={styles["date-time-date"]}>
                  {endDate ? format(endDate, "d") : "-"}
                  <div className={`${styles["date-time-day"]} fw-bold`}>
                    {endDate ? format(endDate, "eeee") : "Select Date"}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles["date-time-clock"]}>
              <CustomTimeDropdown
                options={timeOptions}
                selected={endTime}
                onChange={time => handleTimeChange(time, "end")}
                styles={styles}
                disabled={!endDate}
                disabledOptions={getDisabledEndTimeOptions()}
              />
            </div>
          </div>
        </div>
      </ReactDatePicker>
    </div>
  );
};

export default CustomDateTimePicker;
